import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import petVaccines from '../../data/petVaccines';
import '../../style/EditVaccinePage.css';
import { useTranslation } from 'react-i18next'; // Import translation hook
import i18n from '../../i18n'; // Import i18n instance

const EditVaccinePage = () => {
    const { id, vaccineId } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation(); // Translation function
    const [vaccine, setVaccine] = useState({ name: [], otherName: '' });
    const [loading, setLoading] = useState(false);
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

    useEffect(() => {
        const fetchVaccineDetails = async () => {
            try {
                const storedUser = JSON.parse(localStorage.getItem('user'));
                const token = storedUser?.token;

                if (!token) {
                    throw new Error(t('general.noAuthToken')); // Use translation for error
                }

                const response = await fetch(`${baseUrl}/api/pets/${id}/vaccines/${vaccineId}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                });

                if (!response.ok) {
                    throw new Error(t('vaccine.fetchFailed')); // Use translation for error
                }

                const data = await response.json();
                const otherName = data.name.find(
                    (name) => !petVaccines.some((vaccine) => vaccine.value === name)
                );
                setVaccine({
                    name: data.name.filter((name) => petVaccines.some((vaccine) => vaccine.value === name)),
                    otherName: otherName || '', // Set "Other" name if found
                });
            } catch (error) {
                console.error(error);
            }
        };

        fetchVaccineDetails();
    }, [id, vaccineId, baseUrl, t]);

    const handleVaccineChange = (selectedOptions) => {
        const selectedValues = selectedOptions.map((option) => option.value);
        const isOtherSelected = selectedValues.includes('other');
        setVaccine({
            ...vaccine,
            name: selectedValues,
            otherName: isOtherSelected ? vaccine.otherName : '', // Clear "Other" name if deselected
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setVaccine({ ...vaccine, [name]: value });
    };

    const handleUpdateVaccine = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const storedUser = JSON.parse(localStorage.getItem('user'));
            const token = storedUser?.token;

            if (!token) {
                throw new Error(t('general.noAuthToken')); // Use translation for error
            }

            // Include "Other" vaccine name if provided
            const formattedOtherName = (vaccine.otherName || '').trim().replace(/\s+/g, '_').toLowerCase();
            const vaccineNames = vaccine.name.filter((name) => name !== 'other'); // Exclude "other"
            if (formattedOtherName) {
                vaccineNames.push(formattedOtherName); // Add formatted "Other" name
            }

            const response = await fetch(`${baseUrl}/api/pets/${id}/vaccines/${vaccineId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ name: vaccineNames }),
            });

            if (!response.ok) {
                throw new Error(t('vaccine.updateFailed')); // Use translation for error
            }

            navigate(`/pet/${id}/vaccine/${vaccineId}`);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const formatOtherName = (string) => {
        return string.charAt(0).toUpperCase() + string.slice(1).replace(/_/g, ' '); // Capitalize first letter and replace underscores with spaces
    };

    const handleDeleteVaccine = async () => {
        try {
            setLoading(true);
            const storedUser = JSON.parse(localStorage.getItem('user'));
            const token = storedUser?.token;

            if (!token) {
                throw new Error(t('general.noAuthToken')); // Use translation for error
            }

            const response = await fetch(`${baseUrl}/api/pets/${id}/vaccines/${vaccineId}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error(t('vaccine.deleteFailed')); // Use translation for error
            }

            navigate(`/pet/${id}`);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="edit-vaccine-page bg-dafauna-background rounded-xl shadow-md p-8">
            <h2 className="text-dafauna-darkText text-2xl font-bold text-center mb-4">{t('vaccine.titleEdit')}</h2>
            <form onSubmit={handleUpdateVaccine} className="space-y-4">
                <div className="form-group">
                    <label htmlFor="name" className="block text-dafauna-darkText font-medium mb-2">
                        {t('vaccine.vaccineName')}
                    </label>
                    <Select
                        id="vaccineName"
                        name="vaccineName"
                        isMulti
                        options={[
                            ...petVaccines.map((vaccine) => ({
                                value: vaccine.value,
                                label: vaccine.label[i18n.language], // Use multilingual labels
                            })),
                            ...(vaccine.otherName ? [] : [{ value: 'other', label: t('vaccine.other') }]), // Remove "Other" if `otherName` exists
                        ]}
                        value={[
                            ...vaccine.name.map((name) => ({
                                value: name,
                                label: petVaccines.find((v) => v.value === name)?.label[i18n.language] || name,
                            })),
                            ...(vaccine.otherName
                                ? [{ value: 'other', label: `${t('vaccine.other')} - ${formatOtherName(vaccine.otherName)}` }] // Display "Other - CustomName"
                                : []),
                        ]}
                        onChange={handleVaccineChange}
                        placeholder={t('vaccine.vaccinePlaceholder')}
                        className="form-select border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                    />
                </div>
                {vaccine.name.includes('other') && ( // Show input field if "Other" is selected
                    <div className="form-group">
                        <label htmlFor="otherName" className="block text-dafauna-darkText font-medium mb-2">
                            {t('vaccine.otherNameLabel')}
                        </label>
                        <input
                            type="text"
                            id="otherName"
                            name="otherName"
                            placeholder={t('vaccine.otherNamePlaceholder')}
                            value={vaccine.otherName}
                            onChange={handleInputChange}
                            required
                            className="form-input border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                        />
                    </div>
                )}
                <button
                    type="submit"
                    disabled={loading}
                    className="btn update-vaccine-btn w-full bg-dafauna-primary text-white rounded-md px-4 py-2 hover:bg-dafauna-primaryLight disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                    {loading ? t('vaccine.updating') : t('vaccine.updateButton')}
                </button>
            </form>
            <button
                onClick={handleDeleteVaccine}
                disabled={loading}
                className="btn delete-vaccine-btn w-full bg-red-500 text-white rounded-md px-4 py-2 hover:bg-red-600 disabled:bg-gray-300 disabled:cursor-not-allowed mt-4"
            >
                {loading ? t('vaccine.deleting') : t('vaccine.deleteButton')}
            </button>
        </div>
    );
};

export default EditVaccinePage;